import React from 'react';
import moment from 'moment';
import { Chart, Series, ValueAxis, CommonSeriesSettings, Export, Legend, VisualRange,
  Margin, Title, Subtitle, Tooltip, Grid } from 'devextreme-react/chart';
import { HeadTitle } from '../../components/HeadTitle';
import { Header } from '../../components/Header';
import { SummaryChartFilter } from '../SummaryChartFilter';
import { getChartSummary } from '../../api/summary';
import { Loading } from '../../components/Loading';
import ChartToolTipTemplate from './ChartToolTipTemplate.js';
import { formatChartSummaryData, getMaxScaleValue } from '../../util/functions';
import { chartVenueAxisConfig } from '../../util/constants';

// max properties
const tradeMax = 'tradeMax';
const volumeMax = 'volumeMax';
const notionalMax = 'notionalMax';

class SummaryChartPage extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      queryParam: {},
      title: 'ECN Trade Summary Chart',
      summary: {
        error: null,
        isLoaded: false,
        data: []
      },
      downloadTradeFileName: '',
      downloadVolumeFileName: '',
      downloadNotionalFileName: '',
      isDownloadInactive: false,
      isSearching: false

    };
    this.baseState = this.state;
    this.loadData = this.loadData.bind(this);
    this.setQueryParameters = this.setQueryParameters.bind(this);
    this.handleEmptyOrErrorResults = this.handleEmptyOrErrorResults.bind(this);
    this.clear = this.clear.bind(this);
  }

  setQueryParameters (queryParamObj) {
    this.clear();
    // start the loading icon
    this.setState({ isSearching: true });
    this.setState({ queryParam: queryParamObj }, () => {
      this.loadData();
    });
  }

  loadData () {
    var filters = this.state.queryParam;
    getChartSummary(filters)
      .then(data => {
        formatChartSummaryData(data, 'trades', tradeMax);
        formatChartSummaryData(data, 'volume', volumeMax);
        formatChartSummaryData(data, 'notional', notionalMax);
        data.tradeMaxScale = getMaxScaleValue(data[tradeMax]);
        data.volumeMaxScale = getMaxScaleValue(data[volumeMax]);
        data.notionalMaxScale = getMaxScaleValue(data[notionalMax]);
        //  console.log(data);

        let results = data;

        if (undefined === results || results.length === 0) {
          this.handleEmptyOrErrorResults('No Data Found');
        } else {
          const monthYearText = moment(results.startDate, 'MM/DD/YYYY').format('MMMM_YYYY');
          this.setState(prevState => ({
            summary: {
              ...prevState.summary,
              isLoaded: true,
              error: null,
              data: results
            },
            downloadTradeFileName: `ECN_TRADE_CHART_${monthYearText}`,
            downloadVolumeFileName: `ECN_VOLUME_CHART_${monthYearText}`,
            downloadNotionalFileName: `ECN_NOTIONAL_CHART_${monthYearText}`
          }));
        }
      })
      .catch((error) => {
        // error
        console.log(error);
        var message = (error && error.response &&
          error.response.data && error.response.data) || 'Data currently unavailable';
        this.handleEmptyOrErrorResults(message);
      });
  }

  clear () {
  // just reset the data and not other config
    this.setState(this.baseState);
  }

  handleEmptyOrErrorResults (errorMessage) {
    this.setState(prevState => ({
      summary: {
        ...prevState.summary,
        isLoaded: true,
        data: { trades: [] },
        error: errorMessage
      }
    }));
  }

  render () {
    return (
      <div>
        <HeadTitle title={this.state.title} />
        <Header title={this.state.title} />
        <SummaryChartFilter search={this.setQueryParameters} clear={this.clear} />

        {this.state.isSearching && <Loading
          type='table'
          data={this.state.summary.data.trades}
          loaded={this.state.summary.isLoaded}
          reloading={this.state.summary.isReloading}
          error={this.state.summary.error}
          emptyMessage='No results found.' ><div>
            <Chart id='tradeChart' dataSource={this.state.summary.data.trades} synchronizeMultiAxes={false}>
              <CommonSeriesSettings argumentField='tradeDate' type={'fullstackedbar'} />
              {chartVenueAxisConfig.map(function (item) {
                return (
                  <Series
                    key={item.value}
                    valueField={item.value}
                    name={item.name}
                    color={item.color}
                  />
                );
              })}
              <Margin bottom={20} />
              <Series
                axis='total'
                type='spline'
                valueField='total'
                name='Total'
                color='#1db2f5'
              />
              <ValueAxis>
                <Grid visible />
              </ValueAxis>
              <ValueAxis
                name='total'
                position='right'
                title='Total Trades'
              >
                <VisualRange endValue={this.state.summary.data.tradeMaxScale} />
                <Grid visible />
              </ValueAxis>
              <Legend
                verticalAlignment='bottom'
                horizontalAlignment='center'
                itemTextPosition='bottom'
              />
              <Export enabled fileName={this.state.downloadTradeFileName} />
              <Tooltip
                enabled
                shared
                contentRender={ChartToolTipTemplate}
              />
              <Title text='Trade Summary'>
                <Subtitle text={`${this.state.summary.data.startDate} - ${this.state.summary.data.endDate}`} />
              </Title>
            </Chart>
            <Chart id='volumeChart' dataSource={this.state.summary.data.volume} synchronizeMultiAxes={false}>
              <CommonSeriesSettings argumentField='tradeDate' type={'fullstackedbar'} />
              {chartVenueAxisConfig.map(function (item) {
                return (
                  <Series
                    key={item.value}
                    valueField={item.value}
                    name={item.name}
                    color={item.color}
                  />
                );
              })}
              <Margin bottom={20} />
              <Series
                axis='total'
                type='spline'
                valueField='total'
                name='Total'
                color='#1db2f5'
              />
              <ValueAxis>
                <Grid visible />
              </ValueAxis>
              <ValueAxis
                name='total'
                position='right'
                title='Total Volume'
              >
                <VisualRange endValue={this.state.summary.data.volumeMaxScale} />
                <Grid visible />
              </ValueAxis>
              <Legend
                verticalAlignment='bottom'
                horizontalAlignment='center'
                itemTextPosition='bottom'
              />
              <Export enabled fileName={this.state.downloadVolumeFileName} />
              <Tooltip
                enabled
                shared
                contentRender={ChartToolTipTemplate}
              />
              <Title text='Volume Summary'>
                <Subtitle text={`${this.state.summary.data.startDate} - ${this.state.summary.data.endDate}`} />
              </Title>
            </Chart>
            <Chart id='notionalChart' dataSource={this.state.summary.data.notional} synchronizeMultiAxes={false}>
              <CommonSeriesSettings argumentField='tradeDate' type={'fullstackedbar'} />
              {chartVenueAxisConfig.map(function (item) {
                return (
                  <Series
                    key={item.value}
                    valueField={item.value}
                    name={item.name}
                    color={item.color}
                  />
                );
              })}
              <Margin bottom={20} />
              <Series
                axis='total'
                type='spline'
                valueField='total'
                name='Total'
                color='#1db2f5'
              />
              <VisualRange endValue={this.state.summary.data.notionalMaxScale} />
              <ValueAxis>
                <Grid visible />
              </ValueAxis>
              <ValueAxis
                name='total'
                position='right'
                title='Total Notional'
              >
                <Grid visible />
              </ValueAxis>
              <Legend
                verticalAlignment='bottom'
                horizontalAlignment='center'
                itemTextPosition='bottom'
              />
              <Export enabled fileName={this.state.downloadNotionalFileName} />
              <Tooltip
                enabled
                shared
                contentRender={ChartToolTipTemplate}
              />
              <Title text='Notional Summary'>
                <Subtitle text={`${this.state.summary.data.startDate} - ${this.state.summary.data.endDate}`} />
              </Title>
            </Chart>
          </div>
        </Loading>
        }
      </div>
    );
  }
}
export default SummaryChartPage;
